import React, { useState } from 'react'
import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  LinearProgress,
  Typography,
  makeStyles
} from '@paintscout/ui'
import { SplitDialog } from '../../components'
import type { DialogProps, MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { PRESENTATION_LABEL } from '@paintscout/util'

const topLevelLabel = 'section'
const topLevelLabelPlural = 'sections'
const contentUnitLabel = 'block'
const contentUnitLabelPlural = 'blocks'

const useStyles = makeStyles<Theme, IntroToEditorDialogProps>((theme) => ({
  root: {},
  dialogPaper: {
    minHeight: 469 // make the new photos all the same height and slide it here
  },
  contentRoot: {
    display: 'flex',
    flexDirection: 'column',
    '& p': {
      marginBottom: theme.spacing(1)
    }
  },
  bottomContent: {
    marginTop: 'auto'
  },
  stepCount: {
    marginLeft: 'auto'
  },
  progressBar: {
    height: '6px',
    margin: theme.spacing(1, 0)
  },
  title: {
    margin: theme.spacing(1, 0)
  }
}))

export interface IntroToEditorDialogProps extends DialogProps {
  classes?: StyleClasses<typeof useStyles>
  hideDismissCheckbox?: boolean
  onCancel: () => void
}

function IntroToEditorDialog(props: IntroToEditorDialogProps) {
  const classes = useStyles(props)
  const { open, onCancel, hideDismissCheckbox } = props
  const [step, setStep] = useState(0)
  const [checked, setChecked] = useState(window.localStorage.getItem('isIntroToEditorDialogDismissed') === 'true')
  const totalSteps = 4

  const handleNext = () => {
    if (step + 1 === totalSteps) {
      onCancel()
    } else setStep(step + 1)
  }

  const imgSrc = () => {
    switch (step) {
      case 0:
        return 'https://tpr-sb-static.s3.amazonaws.com/Welcome+Dialog/Welcome.gif'
      case 1:
        return 'https://tpr-sb-static.s3.amazonaws.com/Welcome+Dialog/EditPages.gif'
      case 2:
        return 'https://tpr-sb-static.s3.amazonaws.com/Welcome+Dialog/AddPages.gif'
      case 3:
        return 'https://tpr-sb-static.s3.amazonaws.com/Welcome+Dialog/Style.gif'
    }
  }

  const rightContent = (
    <div style={{ alignItems: 'center', display: 'flex' }}>
      <img
        key={imgSrc()}
        src={imgSrc()}
        alt={'helpful gif'}
        style={{
          height: 'auto',
          maxHeight: '100%',
          maxWidth: '100%',
          verticalAlign: 'top',
          width: 'auto'
        }}
      />
    </div>
  )
  const handleCheckbox = () => {
    if (window.localStorage.getItem('isIntroToEditorDialogDismissed') === 'true' || checked) {
      window.localStorage.setItem('isIntroToEditorDialogDismissed', 'false')
      setChecked(false)
    } else {
      window.localStorage.setItem('isIntroToEditorDialogDismissed', 'true')
      setChecked(true)
    }
  }

  return (
    <SplitDialog
      classes={{ paper: classes.dialogPaper }}
      open={open}
      fullWidth
      maxWidth={'md'}
      rightContent={rightContent}
      onCancel={onCancel}
    >
      <DialogContent classes={{ root: classes.contentRoot }}>
        <LinearProgress
          id="content-top"
          className={classes.progressBar}
          variant={'determinate'}
          value={((step + 1) / totalSteps) * 100}
        />
        <Typography variant={'h2'} className={classes.title}>
          {handleDialogTitle({ step })}
        </Typography>
        {step === 0 && <Welcome />}
        {step === 1 && <EditBlocks />}
        {step === 2 && <AddBlocks />}
        {step === 3 && <Style />}
        <div className={classes.bottomContent}>
          {!hideDismissCheckbox && (
            <FormControlLabel
              label={'Don’t show this again'}
              control={<Checkbox checked={checked} onChange={handleCheckbox} />}
            />
          )}
        </div>
      </DialogContent>
      <DialogActions
        leftButton={
          <Button onClick={() => setStep(step - 1)} variant="text" disabled={step === 0}>
            {'< Back'}
          </Button>
        }
      >
        <Button onClick={handleNext} variant="contained">
          {step === totalSteps - 1 ? 'Got It!' : 'Next'}
        </Button>
      </DialogActions>
    </SplitDialog>
  )
}

export default IntroToEditorDialog

function handleDialogTitle(props: { step: number }) {
  const { step } = props
  switch (step) {
    case 0:
      return `Welcome to Your ${PRESENTATION_LABEL}`
    case 1:
      return `Edit Sections & Blocks`
    case 2:
      return `Add Sections`
    case 3:
      return `Style Your ${PRESENTATION_LABEL}`
  }
}

function Welcome() {
  return (
    <>
      <Typography variant={'body1'}>Here are a few tips to help you get started.</Typography>
      <Typography variant={'body1'}>
        {`Click on a ${contentUnitLabel} (look for the blue outline) and then click on text to make edits.`}
      </Typography>
    </>
  )
}

function EditBlocks() {
  return (
    <>
      <Typography variant={'body1'}>
        {`Your ${PRESENTATION_LABEL} is made up of ${topLevelLabelPlural}. Each ${topLevelLabel} contains one or more ${contentUnitLabelPlural}.`}
      </Typography>
      <Typography variant={'body1'}>
        {`Click on the pencil icon in the top right corner of a ${contentUnitLabel} to access its options.`}
      </Typography>
    </>
  )
}

function AddBlocks() {
  return (
    <>
      <Typography variant={'body1'}>
        {`Add a new ${topLevelLabel} by clicking on the plus icon, found on the outline of the first and last ${contentUnitLabelPlural} in a ${topLevelLabel}.`}
      </Typography>
    </>
  )
}

function Style() {
  return (
    <>
      <Typography variant={'body1'}>
        {`Select Style in the sidebar to edit your ${PRESENTATION_LABEL}'s typography and palette.`}
      </Typography>
      <Typography variant={'body1'}>{`Changes made here will affect your entire ${PRESENTATION_LABEL}.`}</Typography>
    </>
  )
}
